import React, { Suspense, lazy } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  Navigate,
} from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import PageLoader from "../components/PageLoader";
import ProtectedRoute from "../components/ProtectedRoute";
import AdminRoute from "../components/AdminRoute";
import { AuthProvider } from "../context/AuthContext";
import { PermissionProvider } from "../context/PermissionContext";
import AdminLayout from "../layouts/AdminLayout";
import ClientLayout from "../layouts/ClientLayout";
import CodeVaultLayout from "../layouts/CodeVaultLayout";
import AccountsLayout from "../layouts/AccountsLayout";
import HRMSLayout from "../layouts/HRMSLayout";
import SuperAdminLayout from "../layouts/SuperAdminLayout";
import AssetMasterLayout from "../layouts/AssetMasterLayout";
import AdminPermissionRoute from "../components/AdminPermissionRoute";
import AssetMasterRouteGuard from "../components/AssetMasterRouteGuard";
import { useAssetMasterAccess } from "../context/AssetMasterAccessContext";
import LandStageLayout from "../layouts/LandStageLayout";
import DesignLayout from "../layouts/DesignLayout";
import StatutoryStageLayout from "../layouts/StatutoryStageLayout";
import ExecutionStageLayout from "../layouts/ExecutionStageLayout";
import PurchaseLayout from "../layouts/PurchaseLayout";
import RooftopLayout from "../layouts/RooftopLayout";
import PagePermissionRoute from "../components/PagePermissionRoute";
import PurchaseOrderLayout from "../layouts/PurchaseOrderLayout";
import { HrmsAccessProvider } from "../context/HRMSAccessContext";
import { ReimbursementAccessProvider } from "../context/ReimbursementAccessContext";
import InvoiceLayout from "../layouts/InvoiceLayout";
import ReportsLayout from "../layouts/ReportsLayout";
import FinanceLayout from "../layouts/FinanceLayout";
import ProjectFinanceLayout from "../layouts/ProjectFinanceLayout";

const ProjectSelection = lazy(() => import("../pages/ProjectSelection"));
const PartySelection = lazy(() => import("../pages/PartySelection"));
const DocLibrary = lazy(() => import("../pages/DocLibrary"));
const ProjectDocLibrary = lazy(() => import("../pages/ProjectDocLibrary"));

const Login = lazy(() => {
  const prefetchDashboard = () => {
    const Dashboard = import("../pages/Dashboard");
    return Dashboard;
  };

  return import("../pages/Login").then((module) => {
    window.requestIdleCallback?.(prefetchDashboard);
    return module;
  });
});

const Dashboard = lazy(() => import("../pages/Dashboard"));
const Settings = lazy(() => import("../pages/Settings"));
const SelectOrganization = lazy(() => import("../pages/SelectOrganization"));
const ClientInverters = lazy(() => import("../pages/client/Inverters"));
const PlantPerformance = lazy(() => import("../pages/client/PlantPerformance"));
const ABTMeters = lazy(() => import("../pages/client/ABTMeters"));
const LiveData = lazy(() => import("../pages/client/LiveData"));
const NotFound = lazy(() => import("../pages/NotFound"));
const SetPassword = lazy(() => import("../pages/SetPassword"));
const PartyCodeSubmission = lazy(() => import("../pages/PartyCodeSubmission"));
const PartyCodeLinkApproval = lazy(() => import("../pages/PartyCodeLinkApproval"));
const PagePermissionsGrid = lazy(
  () => import("../pages/admin/PagePermissionsGrid")
);
const ProjectTypesAdmin = lazy(
  () => import("../pages/admin/ProjectTypesAdmin")
);
const ProjectsInType = lazy(() => import("../pages/admin/ProjectsInType"));
const ProjectConfig = lazy(() => import("../pages/admin/ProjectConfig"));
const ClientConfiguration = lazy(() => import("../pages/admin/PartyAdmin"));
const RolesAdmin = lazy(() => import("../pages/RolesAdmin"));
const EmployeesAdmin = lazy(() => import("../pages/admin/EmployeesAdmin"));
const UserProjects = lazy(() => import("../pages/UserProjects"));
const ClientAdmin = lazy(() => import("../pages/ClientAdmin"));
const SLDCReports = lazy(() => import("../pages/client/SLDCRepots"));
const ModuleCleaning = lazy(() => import("../pages/client/ModuleCleaning"));
const ChecklistConfig = lazy(() => import("../pages/admin/ChecklistConfig"));
const Checklist = lazy(() => import("../pages/client/Checklist"));
const SpareList = lazy(() => import("../pages/client/SpareList"));
const RooftopDashboard = lazy(() => import("../pages/rooftop/Dashboard"));
const RooftopReports = lazy(() => import("../pages/rooftop/RooftopReports"));
const RooftopInverters = lazy(() => import("../pages/rooftop/Inverters"));
const ExpenseCode = lazy(() => import("../pages/codevault/ExpenseCode"));
const ExpenseCodeConfig = lazy(
  () => import("../pages/codevault/ExpenseCodeConfig")
);
const IncomeCode = lazy(() => import("../pages/codevault/IncomeCode"));
const IncomeCodeConfig = lazy(
  () => import("../pages/codevault/IncomeCodeConfig")
);
const ProjectCode = lazy(() => import("../pages/codevault/ProjectCode"));
const ProjectCodeConfig = lazy(
  () => import("../pages/codevault/ProjectCodeConfig")
);
const PartyCode = lazy(() => import("../pages/codevault/PartyCode"));
const PartyCodeConfig = lazy(
  () => import("../pages/codevault/PartyCodeConfig")
);
const VendorRegistrationLinks = lazy(
  () => import("../pages/codevault/VendorRegistrationLinks")
);
const CodeVaultUsers = lazy(() => import("../pages/superadmin/CodeVaultUsers"));
const CodeVaultAdmin = lazy(() => import("../pages/superadmin/CodeVaultAdmin"));
const HRMSAttendanceLogs = lazy(() => import("../pages/hrms/AttendanceLogs"));
const HRMSEmployeeLogs = lazy(() => import("../pages/hrms/EmployeeLogs"));
const HRMSEmployeeConfig = lazy(() => import("../pages/hrms/EmployeeConfig"));
const HRMSSalarySlips = lazy(() => import("../pages/hrms/SalarySlips"));
const HRMSSalarySlipSettings = lazy(() => import("../pages/hrms/SalarySlipSettings"));
const HRMSReimbursementExports = lazy(() => import("../pages/hrms/ReimbursementExports"));
const HRMSReimbursements = lazy(() => import("../pages/hrms/Reimbursements"));
const HRMSReimbursementCreate = lazy(() => import("../pages/hrms/ReimbursementCreate"));
const HRMSReimbursementView = lazy(() => import("../pages/hrms/ReimbursementView"));
const HRMSReimbursementPendingReview = lazy(() => import("../pages/hrms/ReimbursementPendingReview"));
const HRMSReimbursementPendingReportingManager = lazy(() => import("../pages/hrms/ReimbursementPendingReportingManager"));
const HRMSReimbursementPendingReview2 = lazy(() => import("../pages/hrms/ReimbursementPendingReview2"));
const HRMSReimbursementPendingRecommendation = lazy(() => import("../pages/hrms/ReimbursementPendingRecommendation"));
const HRMSReimbursementPendingApproval = lazy(() => import("../pages/hrms/ReimbursementPendingApproval"));
const HRMSReportingManagers = lazy(() => import("../pages/hrms/ReportingManagers"));
const HRMSAccessManagement = lazy(() => import("../pages/hrms/HrmsAccessManagement"));
const HRMSTeamAttendanceLogs = lazy(() => import("../pages/hrms/TeamAttendanceLogs"));
const ProcessTemplateUsers = lazy(
  () => import("../pages/superadmin/ProcessTemplateUsers")
);
const AssetMasterUsers = lazy(
  () => import("../pages/superadmin/AssetMasterUsers")
);
const InvoiceUsers = lazy(() => import("../pages/superadmin/InvoiceUsers"));
const ReportUsers = lazy(() => import("../pages/superadmin/ReportUsers"));
const AccountsUsers = lazy(() => import("../pages/superadmin/AccountsUsers"));
const RooftopAdmin = lazy(() => import("../pages/superadmin/RooftopAdmin"));
const PODocs = lazy(() => import("../pages/superadmin/PODocs"));
const DatabaseBackups = lazy(
  () => import("../pages/superadmin/DatabaseBackups")
);
const TelemetryDashboard = lazy(
  () => import("../pages/superadmin/TelemetryDashboard")
);
const LiveFeedConfig = lazy(
  () => import("../pages/superadmin/LiveFeedConfig")
);
const SldcDataAdmin = lazy(
  () => import("../pages/superadmin/SldcDataAdmin")
);
const InsuranceReminderConfig = lazy(
  () => import("../pages/superadmin/InsuranceReminderConfig")
);
const ProjectFinanceUsers = lazy(
  () => import("../pages/superadmin/ProjectFinanceUsers")
);
const ProcessTemplateStageSelection = lazy(
  () => import("../pages/processTemplate/ProcessTemplateStageSelection")
);
const ProcessTemplateLayout = lazy(
  () => import("../layouts/ProcessTemplateLayout")
);
const LandDocumentsTemplate = lazy(
  () => import("../pages/processTemplate/LandDocumentsTemplate")
);
const LandQuestionnaireTemplate = lazy(
  () => import("../pages/processTemplate/LandQuestionnaireTemplate")
);
const StatutoryTemplates = lazy(
  () => import("../pages/processTemplate/StatutoryTemplates")
);
const StatutoryQuestionaire = lazy(
  () => import("../pages/processTemplate/StatutoryQuestionaire")
);
const DesignDocumentsTemplate = lazy(
  () => import("../pages/processTemplate/DesignDocumentsTemplate")
);
const DesignDocumentsDisciplinesTemplate = lazy(
  () => import("../pages/processTemplate/DesignDocumentsDisciplinesTemplate")
);
const DesignDocumentsTypeTemplate = lazy(
  () => import("../pages/processTemplate/DesignDocumentsTypeTemplate")
);
const DesignBOMTemplate = lazy(
  () => import("../pages/processTemplate/DesignBOMTemplate")
);
const DesignItemCodesTemplate = lazy(
  () => import("../pages/processTemplate/DesignItemCodesTemplate")
);
const DesignInputFormTemplate = lazy(
  () => import("../pages/processTemplate/DesignInputFormTemplate")
);
const PurchaseDocumentsTemplate = lazy(
  () => import("../pages/processTemplate/PurchaseDocumentsTemplate")
);
const ExecutionDprTemplate = lazy(
  () => import("../pages/processTemplate/ExecutionDprTemplate")
);
const PlantDetails = lazy(() => import("../pages/client/PlantDetails"));
const ContactDetails = lazy(() => import("../pages/client/ContactDetails"));
const Reports = lazy(() => import("../pages/client/Reports"));
const ClientSelectionPage = lazy(
  () => import("../pages/client/ClientSelection")
);
const StageSelection = lazy(() => import("../pages/StageSelection"));
const ClientStageSelection = lazy(
  () => import("../pages/client/ClientStageSelection")
);
const ClientDashboard = lazy(() => import("../pages/ClientDashboard"));
const EmployeeProjects = lazy(
  () => import("../pages/admin/EmployeeProjectsAdmin")
);
const UsersAdmin = lazy(() => import("../pages/superadmin/UsersAdmin"));
const UserPermissionsAdmin = lazy(
  () => import("../pages/superadmin/UserPermissionsAdmin")
);

// Invoice Portal Pages
const InvoiceDashboard = lazy(() => import("../pages/invoice/InvoiceDashboard"));
const InvoiceListPage = lazy(() => import("../pages/invoice/InvoiceListPage"));
const InvoiceFormPage = lazy(() => import("../pages/invoice/InvoiceFormPage"));
const MyInvoicesPage = lazy(() => import("../pages/invoice/MyInvoicesPage"));
const PendingReviewPage = lazy(() => import("../pages/invoice/PendingReviewPage"));
const PendingRecommendation1Page = lazy(() => import("../pages/invoice/PendingRecommendation1Page"));
const PendingRecommendation2Page = lazy(() => import("../pages/invoice/PendingRecommendation2Page"));
const PendingApprovalPage = lazy(() => import("../pages/invoice/PendingApprovalPage"));
const Documents = lazy(() => import("../pages/landstage/Documents"));
const LandStageDashboard = lazy(() => import("../pages/landstage/Dashboard"));
const LandInputForm = lazy(() => import("../pages/landstage/LandInputForm"));
const LandStageSettings = lazy(() => import("../pages/landstage/Settings"));
const LandQuestionnaire = lazy(
  () => import("../pages/landstage/LandQuestionnaire")
);
const DesignDocs = lazy(() => import("../pages/design/DesignDocs"));
const DesignApprovedDocs = lazy(() => import("../pages/design/DesignApprovedDocs"));
const DesignUploadedDocs = lazy(() => import("../pages/design/DesignUploadedDocs"));
const DesignPurchaseDocs = lazy(() => import("../pages/design/DesignPurchaseDocs"));
const DesignDashboard = lazy(() => import("../pages/design/DesignDashboard"));
const DesignBillOfMaterial = lazy(() => import("../pages/design/DesignBillOfMaterial"));
const DesignBillOfMaterialFormPage = lazy(() => import("../pages/design/DesignBillOfMaterialFormPage"));
const DesignBomSubmaterials = lazy(() => import("../pages/design/DesignBomSubmaterials"));
const DesignBomGrid = lazy(() => import("../pages/design/DesignBomGrid"));
const DesignSettings = lazy(() => import("../pages/design/DesignSettings"));
const DesignInputForm = lazy(() => import("../pages/design/DesignInputForm"));
const PurchaseOrders = lazy(
  () => import("../pages/purchaseorder/PurchaseOrders")
);
const AcknowledgedPOs = lazy(
  () => import("../pages/purchaseorder/AcknowledgedPOs")
);
const POExports = lazy(
  () => import("../pages/purchaseorder/POExports")
);
const PurchaseOrderFormPage = lazy(
  () => import("../pages/purchase/PurchaseOrderFormPage")
);
const ManualPurchaseOrderPage = lazy(
  () => import("../pages/purchase/ManualPurchaseOrderPage")
);
const PurchaseOrderLayoutDesignerPage = lazy(
  () => import("../pages/purchase/PurchaseOrderLayoutDesignerPage")
);
const PurchaseOrderTemplatesPage = lazy(
  () => import("../pages/purchase/PurchaseOrderTemplatesPage")
);
const Processes = lazy(() => import("../pages/statutory/Processes"));
const StatutoryStageChatPage = lazy(
  () => import("../pages/statutorystage/StatutoryStageChatPage")
);
const StatutoryQuestionnaire = lazy(
  () => import("../pages/statutory/StatutoryQuestionnaire")
);
const StatutorySelectionLayout = lazy(
  () => import("../layouts/StatutorySelectionLayout")
);
const StatutorySelectionDashboard = lazy(
  () => import("../pages/statutory/StatutorySelectionDashboard")
);
const StatutorySelectionSettings = lazy(
  () => import("../pages/statutory/StatutorySelectionSettings")
);
const StatutoryGeneralForm = lazy(
  () => import("../pages/statutory/StatutoryGeneralForm")
);
const StaffEmployees = lazy(() => import("../pages/superadmin/StaffEmployees"));
const NoPermission = lazy(() => import("../pages/NoPermission"));
const StatutoryStageSelection = lazy(
  () => import("../pages/statutory/StatutoryStageSelection")
);
const ExecutionStageTasks = lazy(
  () => import("../pages/executionstage/Tasks")
);
const ExecutionDeliveries = lazy(
  () => import("../pages/executionstage/Deliveries")
);
const ExecutionDprConfig = lazy(
  () => import("../pages/executionstage/DprConfig")
);
const ExecutionDailyProgress = lazy(
  () => import("../pages/executionstage/DailyProgress")
);
const ExecutionStageSettings = lazy(
  () => import("../pages/executionstage/Settings")
);
const ExecutionDprReports = lazy(
  () => import("../pages/executionstage/DprReports")
);
const RooftopLiveData = lazy(() => import("../pages/rooftop/RooftopLiveData"));
const PurchaseDesignDocs = lazy(
  () => import("../pages/purchase/PurchaseDesignDocs")
);
const PurchaseInputForm = lazy(
  () => import("../pages/purchase/PurchaseInputForm")
);
const PurchaseStageDispatch = lazy(
  () => import("../pages/purchase/PurchaseStageDispatch")
);
const PurchaseBillOfMaterial = lazy(
  () => import("../pages/purchase/PurchaseBillOfMaterial")
);
const PurchaseBomGrid = lazy(
  () => import("../pages/purchase/PurchaseBomGrid")
);
const PurchaseSettings = lazy(
  () => import("../pages/purchase/PurchaseSettings")
);
const CostComparisonList = lazy(
  () => import("../pages/purchase/CostComparisonList")
);
const CostComparisonForm = lazy(
  () => import("../pages/purchase/CostComparisonForm")
);
const CostComparisonDetail = lazy(
  () => import("../pages/purchase/CostComparisonDetail")
);
const ComparisonSummary = lazy(
  () => import("../pages/purchase/ComparisonSummary")
);
const MaterialTrackingDashboard = lazy(
  () => import("../pages/purchase/MaterialTrackingDashboard")
);
const PurchaseCostSheet = lazy(
  () => import("../pages/purchase/PurchaseCostSheet")
);
const VendorQuoteForm = lazy(
  () => import("../pages/VendorQuoteForm")
);
const AssetMasterCategories = lazy(
  () => import("../pages/assetMaster/Categories")
);
const AssetApproval = lazy(
  () => import("../pages/assetMaster/AssetApproval")
);
const AssetApprovalFormPage = lazy(
  () => import("../pages/assetMaster/AssetApprovalFormPage")
);
const AssetMaster = lazy(
  () => import("../pages/assetMaster/AssetMaster")
);
const Assets = lazy(() => import("../pages/assetMaster/Assets"));

// Picks the best landing page inside /assetmaster based on the user's
// permissions. Users with Asset Master / Write access land on the full list;
// plain custodians land on the "My Assets" page.
const AssetMasterIndexRedirect: React.FC = () => {
  const { canAssetMaster, canWrite, canRead, isCustodian } =
    useAssetMasterAccess();
  if (canAssetMaster || canWrite)
    return <Navigate to="/assetmaster/master" replace />;
  if (canRead || isCustodian)
    return <Navigate to="/assetmaster/assets" replace />;
  return <Navigate to="/assetmaster/master" replace />;
};
const AssetDelivery = lazy(
  () => import("../pages/assetMaster/AssetDelivery")
);
const AssetDeliveryFormPage = lazy(
  () => import("../pages/assetMaster/AssetDeliveryFormPage")
);
const PurchaseOrderUsers = lazy(
  () => import("../pages/superadmin/PurchaseOrderUsers")
);
const PurchaseOrderSettings = lazy(
  () => import("../pages/purchaseorder/PurchaseOrderSettings")
);
const PurchaseOrderVendorEmailStatus = lazy(
  () => import("../pages/purchaseorder/PurchaseOrderVendorEmailStatus")
);
const PurchaseTermsAndConditions = lazy(
  () => import("../pages/purchaseorder/PurchaseTermsAndConditions")
);
const PODispatch = lazy(
  () => import("../pages/purchaseorder/PODispatch")
);
const HRMSDepartments = lazy(
  () => import("../pages/superadmin/HRMSDepartments")
);
const HRMSDesignations = lazy(
  () => import("../pages/superadmin/HRMSDesignations")
);
const HRMSUsers = lazy(() => import("../pages/superadmin/HRMSUsers"));
const ReimbursementUsers = lazy(() => import("../pages/superadmin/ReimbursementUsers"));
const ReportsDashboard = lazy(() => import("../pages/reports/ReportsDashboard"));
const PaymentReportList = lazy(() => import("../pages/reports/PaymentReportList"));
const PaymentReportForm = lazy(() => import("../pages/reports/PaymentReportForm"));
const PaymentReportView = lazy(() => import("../pages/reports/PaymentReportView"));
const AccountsDashboard = lazy(() => import("../pages/accounts/AccountsDashboard"));
const AccountsReimbursements = lazy(() => import("../pages/accounts/AccountsReimbursements"));
const AccountsGSTPayment = lazy(() => import("../pages/accounts/AccountsGSTPayment"));
const AccountsTDSPayment = lazy(() => import("../pages/accounts/AccountsTDSPayment"));
const AccountsSettings = lazy(() => import("../pages/accounts/AccountsSettings"));
const AccountsPaymentReports = lazy(() => import("../pages/accounts/AccountsPaymentReports"));
const AccountsPaymentReportView = lazy(() => import("../pages/accounts/AccountsPaymentReportView"));
const AccountsWorkOrders = lazy(() => import("../pages/accounts/AccountsWorkOrders"));
const FinancialChartOfAccounts = lazy(() => import("../pages/accounts/FinancialChartOfAccounts"));
const FinancialLedger = lazy(() => import("../pages/accounts/FinancialLedger"));
const FinancialInvoices = lazy(() => import("../pages/accounts/FinancialInvoices"));
const FinancialOutstanding = lazy(() => import("../pages/accounts/FinancialOutstanding"));
const FinancialTransactions = lazy(() => import("../pages/accounts/FinancialTransactions"));
const PaymentEntry = lazy(() => import("../pages/accounts/PaymentEntry"));
const FinanceDashboard = lazy(() => import("../pages/finance/FinanceDashboard"));
const FinanceSettings = lazy(() => import("../pages/finance/FinanceSettings"));
const FinancePandLStatement = lazy(() => import("../pages/finance/FinancePandLStatement"));
const FinanceGSTStatement = lazy(() => import("../pages/finance/FinanceGSTStatement"));
const FinancePOStatement = lazy(() => import("../pages/finance/FinancePOStatement"));
const FinancePaymentStatement = lazy(() => import("../pages/finance/FinancePaymentStatement"));
const ProjectFinanceDashboard = lazy(() => import("../pages/projectfinance/ProjectFinanceDashboard"));
const ProjectFinanceViewerDashboard = lazy(() => import("../pages/projectFinanceViewer/ProjectFinanceDashboard"));
const ProjectFinancePandLStatement = lazy(() => import("../pages/projectfinance/ProjectFinancePandLStatement"));
const ProjectFinanceGSTStatement = lazy(() => import("../pages/projectfinance/ProjectFinanceGSTStatement"));
const ProjectFinancePOStatement = lazy(() => import("../pages/projectfinance/ProjectFinancePOStatement"));
const ProjectFinancePaymentStatement = lazy(() => import("../pages/projectfinance/ProjectFinancePaymentStatement"));

function ErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  return (
    <div role="alert" style={{ padding: "20px" }}>
      <h2>Something went wrong</h2>
      <pre style={{ color: "red" }}>{error.message}</pre>
      <button onClick={resetErrorBoundary}>Try again</button>
    </div>
  );
}

const router = createBrowserRouter(
  [
    {
      path: "/",
      element: (
        <AuthProvider>
          <PermissionProvider>
            <Outlet />
          </PermissionProvider>
        </AuthProvider>
      ),
      errorElement: (
        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          onReset={() => window.location.reload()}
        />
      ),
      children: [
        {
          path: "login",
          element: (
            <Suspense fallback={<PageLoader />}>
              <Login />
            </Suspense>
          ),
        },
        {
          path: "set-password",
          element: (
            <Suspense fallback={<PageLoader />}>
              <SetPassword />
            </Suspense>
          ),
        },
        {
          path: "vendor-registration",
          element: (
            <Suspense fallback={<PageLoader />}>
              <PartyCodeSubmission />
            </Suspense>
          ),
        },
        {
          path: "vendor-registration-approval",
          element: (
            <Suspense fallback={<PageLoader />}>
              <PartyCodeLinkApproval />
            </Suspense>
          ),
        },
        {
          path: "vendor-quote",
          element: (
            <Suspense fallback={<PageLoader />}>
              <VendorQuoteForm />
            </Suspense>
          ),
        },
        {
          path: "no-permission",
          element: (
            <Suspense fallback={<PageLoader />}>
              <NoPermission />
            </Suspense>
          ),
        },
        {
          path: "select-organization",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <SelectOrganization />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "stage-selection",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <StageSelection />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "client-stage-selection",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <ClientStageSelection />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "stage-selection/general-form",
          element: <Navigate to="/statutory/stage-selection/general-form" replace />,
        },
        {
          path: "statutory/stage-selection/general-form",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <StatutoryGeneralForm />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "client-dashboard",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <ClientDashboard />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "statutory/stage-selection",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <StatutoryStageSelection />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "statutory/selection",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <StatutorySelectionLayout />
              </Suspense>
            </ProtectedRoute>
          ),
          children: [
            {
              index: true,
              element: <Navigate to="/statutory/selection/dashboard" replace />,
            },
            {
              path: "dashboard",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StatutorySelectionDashboard />
                </Suspense>
              ),
            },
            {
              path: "settings",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StatutorySelectionSettings />
                </Suspense>
              ),
            },
            {
              path: "general-form",
              element: <Navigate to="/statutory/stage-selection/general-form" replace />,
            },
          ],
        },
        {
          path: "project-selection",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <ProjectSelection />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "client-selection",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <ClientSelectionPage />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "party-selection",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <PartySelection />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "doc-library",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <DocLibrary />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "project-doc-library",
          element: (
            <ProtectedRoute>
              <AdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <ProjectDocLibrary />
                </Suspense>
              </AdminRoute>
            </ProtectedRoute>
          ),
        },
        {
          path: "super-admin",
          element: (
            <ProtectedRoute>
              <AdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <SuperAdminLayout />
                </Suspense>
              </AdminRoute>
            </ProtectedRoute>
          ),
          children: [
            {
              index: true,
              element: <Navigate to="/super-admin/users" replace />,
            },
            {
              path: "users",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <UsersAdmin />
                </Suspense>
              ),
            },
            {
              path: "users/user-permissions/:userId",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <UserPermissionsAdmin />
                </Suspense>
              ),
            },
            {
              path: "codevault-users",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <CodeVaultUsers />
                </Suspense>
              ),
            },
            {
              path: "codevault-admin",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <CodeVaultAdmin />
                </Suspense>
              ),
            },
            {
              path: "process-template-users",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ProcessTemplateUsers />
                </Suspense>
              ),
            },
            {
              path: "asset-master-users",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <AssetMasterUsers />
                </Suspense>
              ),
            },
            {
              path: "invoice-users",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <InvoiceUsers />
                </Suspense>
              ),
            },
            {
              path: "report-users",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ReportUsers />
                </Suspense>
              ),
            },
            {
              path: "accounts-users",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <AccountsUsers />
                </Suspense>
              ),
            },
            {
              path: "hrms-users",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <HRMSUsers />
                </Suspense>
              ),
            },
            {
              path: "rooftop-admin",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <RooftopAdmin />
                </Suspense>
              ),
            },
            {
              path: "staff-employees",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaffEmployees />
                </Suspense>
              ),
            },
            {
              path: "purchase-order-users",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PurchaseOrderUsers />
                </Suspense>
              ),
            },
            {
              path: "po-docs",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PODocs />
                </Suspense>
              ),
            },
            {
              path: "hrms-departments",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <HRMSDepartments />
                </Suspense>
              ),
            },
            {
              path: "hrms-designations",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <HRMSDesignations />
                </Suspense>
              ),
            },
            {
              path: "reimbursement-users",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ReimbursementUsers />
                </Suspense>
              ),
            },
            {
              path: "login-logs",
              element: (
                <Suspense fallback={<PageLoader />}>
                  {/* <LoginLogsAdmin /> */}
                  <h1>Login Logs</h1>
                </Suspense>
              ),
            },
            {
              path: "smtp-logs",
              element: (
                <Suspense fallback={<PageLoader />}>
                  {/* <EmployeeAdmin /> */}
                  <h1>SMTP Logs</h1>
                </Suspense>
              ),
            },
            {
              path: "database-backups",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <DatabaseBackups />
                </Suspense>
              ),
            },
            {
              path: "telemetry",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <TelemetryDashboard />
                </Suspense>
              ),
            },
            {
              path: "live-feed-config",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <LiveFeedConfig />
                </Suspense>
              ),
            },
            {
              path: "sldc-data",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <SldcDataAdmin />
                </Suspense>
              ),
            },
            {
              path: "insurance-reminders",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <InsuranceReminderConfig />
                </Suspense>
              ),
            },
            {
              path: "project-finance-users",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ProjectFinanceUsers />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "admin",
          element: (
            <ProtectedRoute>
              <AdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminLayout />
                </Suspense>
              </AdminRoute>
            </ProtectedRoute>
          ),
          children: [
            {
              index: true,
              element: <Navigate to="/admin/project-types" replace />,
            },
            {
              path: "clients",
              element: (
                <AdminPermissionRoute pageName="clients">
                  <Suspense fallback={<PageLoader />}>
                    <ClientAdmin />
                  </Suspense>
                </AdminPermissionRoute>
              ),
            },
            {
              path: "page-permissions",
              element: (
                <AdminPermissionRoute pageName="employees">
                  <Suspense fallback={<PageLoader />}>
                    <PagePermissionsGrid />
                  </Suspense>
                </AdminPermissionRoute>
              ),
            },
            {
              path: "project-types",
              element: (
                <AdminPermissionRoute pageName="projects">
                  <Suspense fallback={<PageLoader />}>
                    <ProjectTypesAdmin />
                  </Suspense>
                </AdminPermissionRoute>
              ),
            },
            {
              path: "project-types/:projectTypeId",
              element: (
                <AdminPermissionRoute pageName="projects">
                  <Suspense fallback={<PageLoader />}>
                    <ProjectsInType />
                  </Suspense>
                </AdminPermissionRoute>
              ),
            },
            {
              path: "projects/:projectId/clients/configure",
              element: (
                <AdminPermissionRoute pageName="projects">
                  <Suspense fallback={<PageLoader />}>
                    <ClientConfiguration />
                  </Suspense>
                </AdminPermissionRoute>
              ),
            },
            {
              path: "projects/:projectId/config",
              element: (
                <AdminPermissionRoute pageName="projects">
                  <Suspense fallback={<PageLoader />}>
                    <ProjectConfig />
                  </Suspense>
                </AdminPermissionRoute>
              ),
            },
            {
              path: "employees/employee-projects/:userId",
              element: (
                <AdminPermissionRoute pageName="employees">
                  <Suspense fallback={<PageLoader />}>
                    <EmployeeProjects />
                  </Suspense>
                </AdminPermissionRoute>
              ),
            },
            {
              path: "user-projects",
              element: (
                <AdminPermissionRoute pageName="projects">
                  <Suspense fallback={<PageLoader />}>
                    <UserProjects />
                  </Suspense>
                </AdminPermissionRoute>
              ),
            },
            {
              path: "roles",
              element: (
                <AdminPermissionRoute pageName="roles">
                  <Suspense fallback={<PageLoader />}>
                    <RolesAdmin />
                  </Suspense>
                </AdminPermissionRoute>
              ),
            },
            {
              path: "employees",
              element: (
                <AdminPermissionRoute pageName="employees">
                  <Suspense fallback={<PageLoader />}>
                    <EmployeesAdmin />
                  </Suspense>
                </AdminPermissionRoute>
              ),
            },
            {
              path: "checklist-configuration",
              element: (
                <AdminPermissionRoute pageName="checklist-config">
                  <Suspense fallback={<PageLoader />}>
                    <ChecklistConfig />
                  </Suspense>
                </AdminPermissionRoute>
              ),
            },
          ],
        },
        {
          path: "/",
          element: (
            <ProtectedRoute>
              <ClientLayout />
            </ProtectedRoute>
          ),
          children: [
            {
              path: "",
              element: (
                <PagePermissionRoute pageName="dashboard" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <Dashboard />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "settings",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <Settings />
                </Suspense>
              ),
            },
            {
              path: "inverters",
              element: (
                <PagePermissionRoute pageName="inverters" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <ClientInverters />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "plant-performance",
              element: (
                <PagePermissionRoute pageName="plant-performance" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <PlantPerformance />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "dgr-reports",
              element: (
                <PagePermissionRoute pageName="reports" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <Reports />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "abt-meters",
              element: (
                <PagePermissionRoute pageName="abt-meters" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <ABTMeters />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "live-data",
              element: (
                <PagePermissionRoute pageName="live-data" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <LiveData />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "plant-details",
              element: (
                <PagePermissionRoute pageName="plant-details" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <PlantDetails />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "sldc-reports",
              element: (
                <PagePermissionRoute pageName="sldc" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <SLDCReports />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "spare-list",
              element: (
                <PagePermissionRoute pageName="sparelist" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <SpareList />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "module-cleaning",
              element: (
                <PagePermissionRoute pageName="module-cleaning" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <ModuleCleaning />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "checklist",
              element: (
                <PagePermissionRoute pageName="checklist" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <Checklist />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "contact-details",
              element: (
                <PagePermissionRoute pageName="contact-details" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <ContactDetails />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
          ],
        },
        {
          path: "/assetmaster",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <AssetMasterLayout />
              </Suspense>
            </ProtectedRoute>
          ),
          children: [
            {
              index: true,
              element: <AssetMasterIndexRedirect />,
            },
            {
              path: "categories",
              element: (
                <AssetMasterRouteGuard requiredPermission="write">
                  <Suspense fallback={<PageLoader />}>
                    <AssetMasterCategories />
                  </Suspense>
                </AssetMasterRouteGuard>
              ),
            },
            {
              path: "approval",
              element: (
                <AssetMasterRouteGuard requiredPermission="write">
                  <Suspense fallback={<PageLoader />}>
                    <AssetApproval />
                  </Suspense>
                </AssetMasterRouteGuard>
              ),
            },
            {
              path: "approval/new",
              element: (
                <AssetMasterRouteGuard requiredPermission="write">
                  <Suspense fallback={<PageLoader />}>
                    <AssetApprovalFormPage mode="create" />
                  </Suspense>
                </AssetMasterRouteGuard>
              ),
            },
            {
              path: "approval/view/:id",
              element: (
                <AssetMasterRouteGuard requiredPermission="write">
                  <Suspense fallback={<PageLoader />}>
                    <AssetApprovalFormPage mode="view" />
                  </Suspense>
                </AssetMasterRouteGuard>
              ),
            },
            {
              path: "approval/edit/:id",
              element: (
                <AssetMasterRouteGuard requiredPermission="write">
                  <Suspense fallback={<PageLoader />}>
                    <AssetApprovalFormPage mode="edit" />
                  </Suspense>
                </AssetMasterRouteGuard>
              ),
            },
            {
              path: "master",
              element: (
                <AssetMasterRouteGuard requiredPermission="assetmaster">
                  <Suspense fallback={<PageLoader />}>
                    <AssetMaster />
                  </Suspense>
                </AssetMasterRouteGuard>
              ),
            },
            {
              path: "assets",
              element: (
                <AssetMasterRouteGuard requiredPermission="read">
                  <Suspense fallback={<PageLoader />}>
                    <Assets />
                  </Suspense>
                </AssetMasterRouteGuard>
              ),
            },
            {
              path: "view/:id",
              element: (
                <AssetMasterRouteGuard requiredPermission="assetmaster">
                  <Suspense fallback={<PageLoader />}>
                    <AssetMaster />
                  </Suspense>
                </AssetMasterRouteGuard>
              ),
            },
            {
              path: "deliverystatus",
              element: (
                <AssetMasterRouteGuard requiredPermission="read">
                  <Suspense fallback={<PageLoader />}>
                    <AssetDelivery />
                  </Suspense>
                </AssetMasterRouteGuard>
              ),
            },
            {
              path: "deliverystatus/view/:id",
              element: (
                <AssetMasterRouteGuard requiredPermission="read">
                  <Suspense fallback={<PageLoader />}>
                    <AssetDeliveryFormPage mode="view" />
                  </Suspense>
                </AssetMasterRouteGuard>
              ),
            },
            {
              path: "deliverystatus/edit/:id",
              element: (
                <AssetMasterRouteGuard requiredPermission="read">
                  <Suspense fallback={<PageLoader />}>
                    <AssetDeliveryFormPage mode="edit" />
                  </Suspense>
                </AssetMasterRouteGuard>
              ),
            }
          ],
        },
        {
          path: "/codevault",
          element: (
            <Suspense fallback={<PageLoader />}>
              <CodeVaultLayout />
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: <Navigate to="/codevault/expense-code" replace />,
            },
            {
              path: "expense-code",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ExpenseCode />
                </Suspense>
              ),
            },
            {
              path: "expense-code-config",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ExpenseCodeConfig />
                </Suspense>
              ),
            },
            {
              path: "income-code",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <IncomeCode />
                </Suspense>
              ),
            },
            {
              path: "income-code-config",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <IncomeCodeConfig />
                </Suspense>
              ),
            },
            {
              path: "project-code",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ProjectCode />
                </Suspense>
              ),
            },
            {
              path: "project-code-config",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ProjectCodeConfig />
                </Suspense>
              ),
            },
            {
              path: "party-code",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PartyCode />
                </Suspense>
              ),
            },
            {
              path: "party-code-config",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PartyCodeConfig />
                </Suspense>
              ),
            },
            {
              path: "vendor-registration-links",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <VendorRegistrationLinks />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "/hrms",
          element: (
            <ProtectedRoute>
              <HrmsAccessProvider>
                <Suspense fallback={<PageLoader />}>
                  <HRMSLayout />
                </Suspense>
              </HrmsAccessProvider>
            </ProtectedRoute>
          ),
          children: [
            {
              index: true,
              element: <Navigate to="/hrms/attendance" replace />,
            },
            {
              path: "attendance",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <HRMSAttendanceLogs />
                </Suspense>
              ),
            },
            {
              path: "admin/employee-logs",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <HRMSEmployeeLogs />
                </Suspense>
              ),
            },
            {
              path: "admin/employee-config",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <HRMSEmployeeConfig />
                </Suspense>
              ),
            },
            {
              path: "admin/salary-slips",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <HRMSSalarySlips />
                </Suspense>
              ),
            },
            {
              path: "admin/salary-slip-settings",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <HRMSSalarySlipSettings />
                </Suspense>
              ),
            },
            {
              path: "admin/reimbursement-exports",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <HRMSReimbursementExports />
                </Suspense>
              ),
            },
            {
              path: "admin/reporting-managers",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <HRMSReportingManagers />
                </Suspense>
              ),
            },
            {
              path: "admin/access-management",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <HRMSAccessManagement />
                </Suspense>
              ),
            },
            {
              path: "team/attendance",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <HRMSTeamAttendanceLogs />
                </Suspense>
              ),
            },
            {
              path: "reimbursements",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ReimbursementAccessProvider>
                    <HRMSReimbursements />
                  </ReimbursementAccessProvider>
                </Suspense>
              ),
            },
            {
              path: "reimbursements/create",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ReimbursementAccessProvider>
                    <HRMSReimbursementCreate />
                  </ReimbursementAccessProvider>
                </Suspense>
              ),
            },
            {
              path: "reimbursements/pending-reporting-manager",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ReimbursementAccessProvider>
                    <HRMSReimbursementPendingReportingManager />
                  </ReimbursementAccessProvider>
                </Suspense>
              ),
            },
            {
              path: "reimbursements/pending-review",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ReimbursementAccessProvider>
                    <HRMSReimbursementPendingReview />
                  </ReimbursementAccessProvider>
                </Suspense>
              ),
            },
            {
              path: "reimbursements/pending-review-2",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ReimbursementAccessProvider>
                    <HRMSReimbursementPendingReview2 />
                  </ReimbursementAccessProvider>
                </Suspense>
              ),
            },
            {
              path: "reimbursements/pending-recommendation",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ReimbursementAccessProvider>
                    <HRMSReimbursementPendingRecommendation />
                  </ReimbursementAccessProvider>
                </Suspense>
              ),
            },
            {
              path: "reimbursements/pending-approval",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ReimbursementAccessProvider>
                    <HRMSReimbursementPendingApproval />
                  </ReimbursementAccessProvider>
                </Suspense>
              ),
            },
            {
              path: "reimbursements/:id",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ReimbursementAccessProvider>
                    <HRMSReimbursementView />
                  </ReimbursementAccessProvider>
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "/purchase-order",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <PurchaseOrderLayout />
              </Suspense>
            </ProtectedRoute>
          ),
          children: [
            {
              index: true,
              element: (
                <Navigate to="/purchase-order/orders" replace />
              ),
            },
            {
              path: "orders",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PurchaseOrders />
                </Suspense>
              ),
            },
            {
              path: "acknowledged-pos",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <AcknowledgedPOs />
                </Suspense>
              ),
            },
            {
              path: "orders/new",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PurchaseOrderFormPage mode="create" />
                </Suspense>
              ),
            },
            {
              path: "orders/manual",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ManualPurchaseOrderPage />
                </Suspense>
              ),
            },
            {
              path: "orders/:orderId/edit",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PurchaseOrderFormPage mode="edit" />
                </Suspense>
              ),
            },
            {
              path: "orders/:orderId/view",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PurchaseOrderFormPage mode="view" />
                </Suspense>
              ),
            },
            {
              path: "terms-and-conditions",
              element: (
                <PagePermissionRoute pageName="purchase" action="write">
                  <Suspense fallback={<PageLoader />}>
                    <PurchaseTermsAndConditions />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "dispatch",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PODispatch />
                </Suspense>
              ),
            },
            {
              path: "settings",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PurchaseOrderSettings />
                </Suspense>
              )
            },
            {
              path: "vendor-email-status",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PurchaseOrderVendorEmailStatus />
                </Suspense>
              )
            },
            {
              path: "exports",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <POExports />
                </Suspense>
              )
            }
          ],
        },
        {
          path: "/rooftop",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <RooftopLayout />
              </Suspense>
            </ProtectedRoute>
          ),
          children: [
            {
              index: true,
              element: <Navigate to="/rooftop/dashboard" replace />,
            },
            {
              path: "dashboard",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <RooftopDashboard />
                </Suspense>
              ),
            },
            {
              path: "live-data",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <RooftopLiveData />
                </Suspense>
              ),
            },
            {
              path: "reports",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <RooftopReports />
                </Suspense>
              ),
            },
            {
              path: "inverters",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <RooftopInverters />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "/process-template",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <Outlet />
              </Suspense>
            </ProtectedRoute>
          ),
          children: [
            {
              index: true,
              element: (
                <Navigate to="/process-template/stage-selection" replace />
              ),
            },
            {
              path: "stage-selection",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ProcessTemplateStageSelection />
                </Suspense>
              ),
            },
            {
              path: "land",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ProcessTemplateLayout />
                </Suspense>
              ),
              children: [
                {
                  index: true,
                  element: <Navigate to="documents" replace />,
                },
                {
                  path: "documents",
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <LandDocumentsTemplate />
                    </Suspense>
                  ),
                },
                {
                  path: "questionnaire",
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <LandQuestionnaireTemplate />
                    </Suspense>
                  ),
                },
              ],
            },
            {
              path: "statutory",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ProcessTemplateLayout />
                </Suspense>
              ),
              children: [
                {
                  index: true,
                  element: <Navigate to="stages" replace />,
                },
                {
                  path: "stages",
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <StatutoryTemplates />
                    </Suspense>
                  ),
                },
                {
                  path: "questionnaire",
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <StatutoryQuestionaire />
                    </Suspense>
                  ),
                },
              ],
            },
            {
              path: "design",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ProcessTemplateLayout />
                </Suspense>
              ),
              children: [
                {
                  index: true,
                  element: <Navigate to="documents" replace />,
                },
                {
                  path: "documents",
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <DesignDocumentsTemplate />
                    </Suspense>
                  ),
                },
                {
                  path: "disciplines",
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <DesignDocumentsDisciplinesTemplate />
                    </Suspense>
                  ),
                },
                {
                  path: "types",
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <DesignDocumentsTypeTemplate />
                    </Suspense>
                  ),
                },
                {
                  path: "bom",
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <DesignBOMTemplate />
                    </Suspense>
                  ),
                },
                {
                  path: "item-codes",
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <DesignItemCodesTemplate />
                    </Suspense>
                  ),
                },
                {
                  path: "input-form",
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <DesignInputFormTemplate />
                    </Suspense>
                  ),
                },
              ],
            },
            {
              path: "purchase",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ProcessTemplateLayout />
                </Suspense>
              ),
              children: [
                {
                  index: true,
                  element: <Navigate to="documents" replace />,
                },
                {
                  path: "documents",
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <PurchaseDocumentsTemplate />
                    </Suspense>
                  ),
                },
              ],
            },
            {
              path: "execution",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ProcessTemplateLayout />
                </Suspense>
              ),
              children: [
                {
                  index: true,
                  element: <Navigate to="dpr" replace />,
                },
                {
                  path: "dpr",
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <ExecutionDprTemplate />
                    </Suspense>
                  ),
                },
              ],
            },
          ],
        },
        {
          path: "/land-stage",
          element: (
            <Suspense fallback={<PageLoader />}>
              <LandStageLayout />
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: <Navigate to="/land-stage/dashboard" replace />,
            },
            {
              path: "dashboard",
              element: (
                <PagePermissionRoute pageName="land-dashboard" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <LandStageDashboard />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "documents",
              element: (
                <PagePermissionRoute pageName="land-documents" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <Documents />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "questionnaire",
              element: (
                <PagePermissionRoute
                  pageName="land-questionnaire"
                  action="read"
                >
                  <Suspense fallback={<PageLoader />}>
                    <LandQuestionnaire />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "settings",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <LandStageSettings />
                </Suspense>
              ),
            },
            {
              path: "input-form",
              element: (
                <PagePermissionRoute pageName="land-documents" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <LandInputForm />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
          ],
        },
        {
          path: "/design-stage",
          element: (
            <Suspense fallback={<PageLoader />}>
              <DesignLayout />
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: <Navigate to="/design-stage/dashboard" replace />,
            },
            {
              path: "documents",
              element: (
                <PagePermissionRoute pageName="design-documents" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <DesignDocs />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "approved-docs",
              element: (
                <PagePermissionRoute pageName="design-documents" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <DesignApprovedDocs />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "uploaded-docs",
              element: (
                <PagePermissionRoute pageName="design-documents" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <DesignUploadedDocs />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "purchase-docs",
              element: (
                <PagePermissionRoute pageName="design-documents" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <DesignPurchaseDocs />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "dashboard",
              element: (
                <PagePermissionRoute pageName="design-documents" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <DesignDashboard />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "design-bom",
              element: (
                <PagePermissionRoute pageName="design-bom" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <DesignBillOfMaterial />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "design-bom/:materialId",
              element: (
                <PagePermissionRoute pageName="design-bom" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <DesignBillOfMaterialFormPage />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "design-bom/:materialId/submaterials",
              element: (
                <PagePermissionRoute pageName="design-bom" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <DesignBomGrid />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "design-bom/:materialId/submaterials-legacy",
              element: (
                <PagePermissionRoute pageName="design-bom" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <DesignBomSubmaterials />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "settings",
              element: (
                <PagePermissionRoute pageName="design-settings" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <DesignSettings />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "input-form",
              element: (
                <PagePermissionRoute pageName="design-documents" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <DesignInputForm />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
          ],
        },
        {
          path: "/invoices",
          element: (
            <Suspense fallback={<PageLoader />}>
              <InvoiceLayout />
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: <Navigate to="/invoices/dashboard" replace />,
            },
            {
              path: "dashboard",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <InvoiceDashboard />
                </Suspense>
              ),
            },
            {
              path: "list",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <InvoiceListPage />
                </Suspense>
              ),
            },
            {
              path: "my-invoices",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <MyInvoicesPage />
                </Suspense>
              ),
            },
            {
              path: "new",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <InvoiceFormPage mode="create" />
                </Suspense>
              ),
            },
            {
              path: "edit/:invoiceId",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <InvoiceFormPage mode="edit" />
                </Suspense>
              ),
            },
            {
              path: "view/:invoiceId",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <InvoiceFormPage mode="view" />
                </Suspense>
              ),
            },
            {
              path: "pending-review",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PendingReviewPage />
                </Suspense>
              ),
            },
            {
              path: "pending-recommendation-1",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PendingRecommendation1Page />
                </Suspense>
              ),
            },
            {
              path: "pending-recommendation-2",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PendingRecommendation2Page />
                </Suspense>
              ),
            },
            {
              path: "pending-approval",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PendingApprovalPage />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "/purchase",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <PurchaseLayout />
              </Suspense>
            </ProtectedRoute>
          ),
          children: [
            {
              index: true,
              element: <Navigate to="/purchase/purchase-design-docs" replace />,
            },
            {
              path: "purchase-design-docs",
              element: (
                // <PagePermissionRoute pageName="purchase-design-docs" action="read">
                <Suspense fallback={<PageLoader />}>
                  <PurchaseDesignDocs />
                </Suspense>
                // </PagePermissionRoute>
              ),
            },
            {
              path: "purchase-bom",
              element: (
                <PagePermissionRoute pageName="purchase-bom" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <PurchaseBillOfMaterial />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "purchase-bom/:materialId/submaterials",
              element: (
                <PagePermissionRoute pageName="purchase-bom" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <PurchaseBomGrid />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "settings",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PurchaseSettings />
                </Suspense>
              ),
            },
            {
              path: "dispatches",
              element: (
                <PagePermissionRoute pageName="purchasestage-dispatches" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <PurchaseStageDispatch />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "input-form",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PurchaseInputForm />
                </Suspense>
              ),
            },
            {
              path: "templates",
              children: [
                {
                  index: true,
                  element: (
                    <PagePermissionRoute pageName="purchase" action="write">
                      <Suspense fallback={<PageLoader />}>
                        <PurchaseOrderTemplatesPage />
                      </Suspense>
                    </PagePermissionRoute>
                  ),
                },
                {
                  path: "new",
                  element: (
                    <PagePermissionRoute pageName="purchase" action="write">
                      <Suspense fallback={<PageLoader />}>
                        <PurchaseOrderLayoutDesignerPage />
                      </Suspense>
                    </PagePermissionRoute>
                  ),
                },
                {
                  path: ":templateId/edit",
                  element: (
                    <PagePermissionRoute pageName="purchase" action="write">
                      <Suspense fallback={<PageLoader />}>
                        <PurchaseOrderLayoutDesignerPage />
                      </Suspense>
                    </PagePermissionRoute>
                  ),
                },
              ],
            },
            {
              path: "material-tracking",
              element: (
                <PagePermissionRoute pageName="cost-comparison" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <MaterialTrackingDashboard />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "cost-sheet",
              element: (
                <PagePermissionRoute pageName="purchase-bom" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <PurchaseCostSheet />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "cost-comparison",
              children: [
                {
                  index: true,
                  element: (
                    <PagePermissionRoute pageName="cost-comparison" action="read">
                      <Suspense fallback={<PageLoader />}>
                        <CostComparisonList />
                      </Suspense>
                    </PagePermissionRoute>
                  ),
                },
                {
                  path: "create",
                  element: (
                    <PagePermissionRoute pageName="cost-comparison" action="write">
                      <Suspense fallback={<PageLoader />}>
                        <CostComparisonForm />
                      </Suspense>
                    </PagePermissionRoute>
                  ),
                },
                {
                  path: ":id",
                  element: (
                    <PagePermissionRoute pageName="cost-comparison" action="read">
                      <Suspense fallback={<PageLoader />}>
                        <CostComparisonDetail />
                      </Suspense>
                    </PagePermissionRoute>
                  ),
                },
                {
                  path: ":id/edit",
                  element: (
                    <PagePermissionRoute pageName="cost-comparison" action="write">
                      <Suspense fallback={<PageLoader />}>
                        <CostComparisonForm />
                      </Suspense>
                    </PagePermissionRoute>
                  ),
                },
                {
                  path: ":id/summary",
                  element: (
                    <PagePermissionRoute pageName="cost-comparison" action="read">
                      <Suspense fallback={<PageLoader />}>
                        <ComparisonSummary />
                      </Suspense>
                    </PagePermissionRoute>
                  ),
                },
              ],
            },
          ],
        },
        {
          path: "/statutory-stage",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <StatutoryStageLayout />
              </Suspense>
            </ProtectedRoute>
          ),
          children: [
            {
              index: true,
              element: <Navigate to="/statutory-stage/processes" replace />,
            },
            {
              path: "processes",
              element: (
                <PagePermissionRoute pageName="processes" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <Processes />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "questionnaire",
              element: (
                <PagePermissionRoute
                  pageName="statutory-questionnaire"
                  action="read"
                >
                  <Suspense fallback={<PageLoader />}>
                    <StatutoryQuestionnaire />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "discussion",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StatutoryStageChatPage />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "/execution-stage",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <ExecutionStageLayout />
              </Suspense>
            </ProtectedRoute>
          ),
          children: [
            {
              index: true,
              element: <Navigate to="/execution-stage/daily-progress" replace />,
            },
            {
              path: "tasks",
              element: (
                <PagePermissionRoute pageName="execution-tasks" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <ExecutionStageTasks />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "deliveries",
              element: (
                <PagePermissionRoute pageName="execution-deliveries" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <ExecutionDeliveries />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "dpr-config",
              element: (
                <PagePermissionRoute pageName="dpr-config" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <ExecutionDprConfig />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "daily-progress",
              element: (
                <PagePermissionRoute pageName="daily-progress" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <ExecutionDailyProgress />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "dpr-reports",
              element: (
                <PagePermissionRoute pageName="dpr-reports" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <ExecutionDprReports />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "settings",
              element: (
                <PagePermissionRoute pageName="execution-settings" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <ExecutionStageSettings />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
          ],
        },
        {
          path: "/accounts",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <AccountsLayout />
              </Suspense>
            </ProtectedRoute>
          ),
          children: [
            {
              index: true,
              element: <Navigate to="/accounts/dashboard" replace />,
            },
            {
              path: "dashboard",
              element: (
                  <Suspense fallback={<PageLoader />}>
                    <AccountsDashboard />
                  </Suspense>
              ),
            },
            {
              path: "reimbursements",
              element: (
                  <Suspense fallback={<PageLoader />}>
                    <AccountsReimbursements />
                  </Suspense>
              ),
            },
            {
              path: "gst-payment",
              element: (
                  <Suspense fallback={<PageLoader />}>
                    <AccountsGSTPayment />
                  </Suspense>
              ),
            },
            {
              path: "tds-payment",
              element: (
                  <Suspense fallback={<PageLoader />}>
                    <AccountsTDSPayment />
                  </Suspense>
              ),
            },
            {
              path: "payment-reports",
              element: (
                  <Suspense fallback={<PageLoader />}>
                    <AccountsPaymentReports />
                  </Suspense>
              ),
            },
            {
              path: "payment-reports/:id",
              element: (
                  <Suspense fallback={<PageLoader />}>
                    <AccountsPaymentReportView />
                  </Suspense>
              ),
            },
            {
              path: "work-orders",
              element: (
                  <Suspense fallback={<PageLoader />}>
                    <AccountsWorkOrders />
                  </Suspense>
              ),
            },
            {
              path: "settings",
              element: (
                  <Suspense fallback={<PageLoader />}>
                    <AccountsSettings />
                  </Suspense>
              ),
            },
            {
              path: "financial/chart-of-accounts",
              element: (
                <PagePermissionRoute pageName="accounts-financial-chart-of-accounts" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <FinancialChartOfAccounts />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "financial/ledger",
              element: (
                <PagePermissionRoute pageName="accounts-financial-ledger" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <FinancialLedger />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "financial/invoices",
              element: (
                <PagePermissionRoute pageName="accounts-financial-invoices" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <FinancialInvoices />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "financial/outstanding",
              element: (
                <PagePermissionRoute pageName="accounts-financial-outstanding" action="read">
                  <Suspense fallback={<PageLoader />}>
                    <FinancialOutstanding />
                  </Suspense>
                </PagePermissionRoute>
              ),
            },
            {
              path: "financial/transactions",
              element: (
                  <Suspense fallback={<PageLoader />}>
                    <FinancialTransactions />
                  </Suspense>
              ),
            },
            {
              path: "payment-entry",
              element: (
                  <Suspense fallback={<PageLoader />}>
                    <PaymentEntry />
                  </Suspense>
              ),
            },
          ],
        },
        {
          path: "/finance",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <FinanceLayout />
              </Suspense>
            </ProtectedRoute>
          ),
          children: [
            {
              index: true,
              element: <Navigate to="/finance/dashboard" replace />,
            },
            {
              path: "dashboard",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <FinanceDashboard />
                </Suspense>
              ),
            },
            {
              path: "pandlstatement",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <FinancePandLStatement />
                </Suspense>
              ),
            },
            {
              path: "gst-statement",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <FinanceGSTStatement />
                </Suspense>
              ),
            },
            {
              path: "po-statement",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <FinancePOStatement />
                </Suspense>
              ),
            },
            {
              path: "payment-statement",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <FinancePaymentStatement />
                </Suspense>
              ),
            },
            {
              path: "settings",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <FinanceSettings />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "/project-finance",
          element: (
            <AdminRoute>
              <Suspense fallback={<PageLoader />}>
                <ProjectFinanceLayout />
              </Suspense>
            </AdminRoute>
          ),
          children: [
            {
              index: true,
              element: <Navigate to="/project-finance/dashboard" replace />,
            },
            {
              path: "dashboard",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ProjectFinanceDashboard />
                </Suspense>
              ),
            },
            {
              path: "pandlstatement",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ProjectFinancePandLStatement />
                </Suspense>
              ),
            },
            {
              path: "gst-statement",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ProjectFinanceGSTStatement />
                </Suspense>
              ),
            },
            {
              path: "po-statement",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ProjectFinancePOStatement />
                </Suspense>
              ),
            },
            {
              path: "payment-statement",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ProjectFinancePaymentStatement />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "/project-finance-viewer",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <ProjectFinanceViewerDashboard />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "/reports",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <ReportsLayout />
              </Suspense>
            </ProtectedRoute>
          ),
          children: [
            {
              index: true,
              element: <Navigate to="/reports/dashboard" replace />,
            },
            {
              path: "dashboard",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ReportsDashboard />
                </Suspense>
              ),
            },
            {
              path: "payment-reports",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PaymentReportList />
                </Suspense>
              ),
            },
            {
              path: "payment-reports/new",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PaymentReportForm mode="create" />
                </Suspense>
              ),
            },
            {
              path: "payment-reports/:id",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PaymentReportView />
                </Suspense>
              ),
            },
            {
              path: "payment-reports/:id/edit",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PaymentReportForm mode="edit" />
                </Suspense>
              ),
            },
            {
              path: "my-reports",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PaymentReportList myReportsOnly />
                </Suspense>
              ),
            },
            {
              path: "pending-review",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PaymentReportList pendingStatus="pending_review" />
                </Suspense>
              ),
            },
            {
              path: "pending-review-2",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PaymentReportList pendingStatus="pending_review_2" />
                </Suspense>
              ),
            },
            {
              path: "pending-recommendation",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PaymentReportList pendingStatus="pending_recommendation" />
                </Suspense>
              ),
            },
            {
              path: "approved-reports",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PaymentReportList pendingStatus="approved" recommenderOnly />
                </Suspense>
              ),
            },
            {
              path: "pending-sanction",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PaymentReportList pendingStatus="pending_sanction_1" />
                </Suspense>
              ),
            },
            {
              path: "pending-sanction-level-2",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PaymentReportList pendingStatus="pending_sanction_2" />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "*",
          element: (
            <Suspense fallback={<PageLoader />}>
              <NotFound />
            </Suspense>
          ),
        },
      ],
    },
  ],
  {
    basename: process.env.PUBLIC_URL || "/",
  }
);

export default function AppRouter() {
  return <RouterProvider router={router} />;
}
